import { useState, useEffect, Dispatch } from "react";
import { StopEventPropagation } from "../../../helper/ui/methods";
import { UserInterface } from "../../../middleware/interfaces/user/user-interface";
import {
  FormatDateBE,
  LocalHourToUtcTime,
  UtcTimeToLocalHour,
} from "../../../helper/general/methods";
import { ForgeCreateInterface } from "../../../middleware/interfaces/forge/forge-create-interface";
import {
  ForgeInterface,
  ForgeRunFrequency,
  ForgeStatus,
} from "../../../middleware/interfaces/forge/forge-interface";
import { UserDecisionInterface } from "../../../middleware/interfaces/user/user-decision-interface";

// A new forge defaults to 2 AM on the viewer's own clock rather than to a fixed
// UTC hour. Overnight is what people want for a large transfer, and "overnight"
// is a local idea -- a UTC default would be the small hours for some viewers and
// the middle of the working day for others.
const FORGE_DEFAULT_RUN_HOUR = 2;

// Whole hours only. The cron ticks every fifteen minutes, so anything finer
// would be a precision the schedule cannot honour.
const FORGE_RUN_HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const FormatHourLabel = (hour: number): string => {
  const suffix = hour < 12 ? "AM" : "PM";
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelveHour}:00 ${suffix}`;
};

interface forgeDialogProps {
  reveal: string;
  user: UserInterface;
  setForgeDialogReveal: Dispatch<React.SetStateAction<string>>;
  selectedForge: ForgeInterface | undefined | null;
  dispatch: Dispatch<any>;
}

const ForgeDialog: React.FC<forgeDialogProps> = ({
  reveal,
  user,
  setForgeDialogReveal,
  selectedForge,
  dispatch,
}) => {
  const [forgeName, setForgeName] = useState<string>("");
  const [forgeOrigin, setForgeOrigin] = useState<string>("");
  const [forgeDestination, setForgeDestination] = useState<string>("");
  const [forgeRunFrequency, setForgeRunFrequency] =
    useState<ForgeRunFrequency>("DAILY");
  const [forgeStatus, setForgeStatus] = useState<ForgeStatus>("ACTIVE");
  // Held as the viewer's own hour, converted to UTC only on the way out.
  const [forgeRunHour, setForgeRunHour] = useState<number>(
    FORGE_DEFAULT_RUN_HOUR
  );
  const [forgeDescription, setForgeDescription] = useState<string>("");

  const HandleCancelClick = () => {
    setForgeDialogReveal("");
  };

  const ForgeCreate = () => {
    const forgeCreateData: ForgeCreateInterface = {
      email: user.email,
      projectID: user.openProject?.projectID!,
      projectUserRole: user.openProject?.projectUserRole!,
      forgeName: forgeName,
      forgeOrigin: forgeOrigin,
      forgeDestination: forgeDestination,
      forgeRunFrequency: forgeRunFrequency,
      forgeRunTime: LocalHourToUtcTime(forgeRunHour),
      forgeDescription: forgeDescription,
      forgeCreationDate: FormatDateBE(new Date()),
      type: "ForgeCreateInterface",
    };
    dispatch({ type: "CREATE_FORGE", payload: forgeCreateData });
    setForgeDialogReveal("");
  };

  const ForgeEdit = () => {
    setForgeDialogReveal("");
    const decision: UserDecisionInterface = {
      title: "Save changes?",
      message: "The forge runs on its new settings from the next run onward.",
      confirmLabel: "Save",
      decisionOptions: ["EDIT_FORGE", "NO_ACTION"],
      result: "",
      data: {
        email: user.email,
        projectUserRole: user.openProject?.projectUserRole!,
        projectID: user.openProject?.projectID!,
        forgeID: selectedForge?.forgeID!,
        forgeName: forgeName,
        forgeDescription: forgeDescription,
        forgeRunFrequency: forgeRunFrequency,
        forgeRunTime: LocalHourToUtcTime(forgeRunHour),
        forgeStatus: forgeStatus,
        type: "ForgeEditInterface",
      },
      type: "UserDecisionInterface",
    };
    dispatch({ type: "DECISION", decision: decision });
  };

  useEffect(() => {
    if (reveal === "EDIT") {
      setForgeName(selectedForge?.forgeName!);
      setForgeRunFrequency(selectedForge?.forgeRunFrequency ?? "DAILY");
      // BROKEN is set by the runner, never chosen here, so it is not one of the
      // options below -- seeding the select with it would leave the field blank
      // and submit a status the editor refuses. A broken forge opens as Active,
      // which is also what saving it should mean: try again.
      setForgeStatus(selectedForge?.forgeStatus === "PAUSED" ? "PAUSED" : "ACTIVE");
      setForgeRunHour(
        selectedForge?.forgeRunTime
          ? UtcTimeToLocalHour(selectedForge.forgeRunTime)
          : FORGE_DEFAULT_RUN_HOUR
      );
      setForgeDescription(selectedForge?.forgeDescription ?? "");
    } else {
      setForgeName("");
      setForgeOrigin("");
      setForgeDestination("");
      setForgeRunFrequency("DAILY");
      setForgeStatus("ACTIVE");
      setForgeRunHour(FORGE_DEFAULT_RUN_HOUR);
      setForgeDescription("");
    }
  }, [reveal]);

  if (!reveal) {
    return null;
  }
  if (reveal === "CREATE") {
    return (
      <div className="dialogCover" onClick={StopEventPropagation}>
        <div className="dialog dialogWide">
          <div className="dialogHeader">
            <div className="dialogHeading">
              <h2 className="dialogTitle">New forge</h2>
              <span className="dialogSubtitle">Keeps a folder in step with a connected source</span>
            </div>
            <span className="dialogClose" onClick={HandleCancelClick}>
              ×
            </span>
          </div>
          <div className="dialogBody dialogFieldGrid">
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Name</span>
              <input
                type="text"
                name="forgeName"
                placeholder="Forge Name"
                value={forgeName}
                onChange={(e) => setForgeName(e.target.value)}
              />
            </div>
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Origin</span>
              <input
                type="text"
                name="forgeOrigin"
                placeholder={"ADSK|TC|DC\\Project\\Folder"}
                title={
                  "Copy from any platform to any other. Start the path with ADSK (Autodesk), TC (Trimble Connect) or DC (DigitalCharcoal)."
                }
                value={forgeOrigin}
                onChange={(e) => setForgeOrigin(e.target.value)}
              />
            </div>
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Destination</span>
              <input
                type="text"
                name="forgeDestination"
                placeholder={"ADSK|TC|DC\\Project\\Folder"}
                title={
                  "Copy from any platform to any other. Start the path with ADSK (Autodesk), TC (Trimble Connect) or DC (DigitalCharcoal)."
                }
                value={forgeDestination}
                onChange={(e) => setForgeDestination(e.target.value)}
              />
            </div>
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Frequency</span>
              <select
                name="forgeRunFrequency"
                value={forgeRunFrequency}
                onChange={(e) =>
                  setForgeRunFrequency(e.target.value as ForgeRunFrequency)
                }
              >
                <option value="DAILY">Daily</option>
                <option value="EVERY_3_DAYS">Every 3 days</option>
                <option value="WEEKLY">Weekly</option>
              </select>
            </div>
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Runs at</span>
              <select
                name="forgeRunTime"
                value={forgeRunHour}
                onChange={(e) => setForgeRunHour(Number(e.target.value))}
              >
                {FORGE_RUN_HOURS.map((hour) => (
                  <option key={hour} value={hour}>
                    {FormatHourLabel(hour)}
                  </option>
                ))}
              </select>
            </div>
            <div className="dialogTextInputWrapper">
              <textarea
                name="forgeDescription"
                placeholder="Forge description..."
                value={forgeDescription}
                onChange={(e) => setForgeDescription(e.target.value)}
              />
            </div>
          </div>
          <div className="dialogButtonWrapper">
            <input
              type="button"
              name="cancel"
              value="Cancel"
              onClick={HandleCancelClick}
            />
            <input
              className="dialogPrimaryButton"
              type="button"
              name="create"
              value="Create"
              onClick={ForgeCreate}
            />
          </div>
        </div>
      </div>
    );
  } else if (reveal === "EDIT") {
    return (
      <div className="dialogCover" onClick={StopEventPropagation}>
        <div className="dialog dialogWide">
          <div className="dialogHeader">
            <div className="dialogHeading">
              <h2 className="dialogTitle">Edit forge</h2>
              <span className="dialogSubtitle">Keeps a folder in step with a connected source</span>
            </div>
            <span className="dialogClose" onClick={HandleCancelClick}>
              ×
            </span>
          </div>
          <div className="dialogBody dialogFieldGrid">
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Name</span>
              <input
                type="text"
                name="forgeName"
                placeholder="Forge Name"
                value={forgeName}
                onChange={(e) => setForgeName(e.target.value)}
              />
            </div>
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Frequency</span>
              <select
                name="forgeRunFrequency"
                value={forgeRunFrequency}
                onChange={(e) =>
                  setForgeRunFrequency(e.target.value as ForgeRunFrequency)
                }
              >
                <option value="DAILY">Daily</option>
                <option value="EVERY_3_DAYS">Every 3 days</option>
                <option value="WEEKLY">Weekly</option>
              </select>
            </div>
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Runs at</span>
              <select
                name="forgeRunTime"
                value={forgeRunHour}
                onChange={(e) => setForgeRunHour(Number(e.target.value))}
              >
                {FORGE_RUN_HOURS.map((hour) => (
                  <option key={hour} value={hour}>
                    {FormatHourLabel(hour)}
                  </option>
                ))}
              </select>
            </div>
            <div className="dialogValueWrapper">
              <span className="dialogInputLabel">Status</span>
              <select
                name="forgeStatus"
                value={forgeStatus}
                onChange={(e) => setForgeStatus(e.target.value as ForgeStatus)}
              >
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
              </select>
            </div>
            <div className="dialogTextInputWrapper">
              <textarea
                name="forgeDescription"
                placeholder="Forge description..."
                value={forgeDescription}
                onChange={(e) => setForgeDescription(e.target.value)}
              />
            </div>
          </div>
          <div className="dialogButtonWrapper">
            <input
              type="button"
              name="cancel"
              value="Cancel"
              onClick={HandleCancelClick}
            />
            <input type="button" name="edit" value="Edit" onClick={ForgeEdit} />
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export default ForgeDialog;
